import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  IconButton,
  Box,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";

/**
 * Add to Cart Button component
 * Allows users to add products to cart with quantity control
 *
 * @param {Object} props Component props
 * @param {string} props.productId Product ID to add to cart
 * @param {number} props.stock Available stock quantity
 * @param {number} props.minOrderQuantity Minimum order quantity
 * @param {Object} props.options Additional options for the cart item
 * @param {boolean} props.fullWidth Whether button should be full width
 * @param {boolean} props.showQuantity Whether to show quantity controls
 * @param {string} props.buttonText Custom button text
 * @returns {JSX.Element} AddToCartButton component
 */
const AddToCartButton = ({
  productId,
  stock,
  minOrderQuantity = 1,
  options = {},
  fullWidth = false,
  showQuantity = true,
  buttonText,
  sx = {},
  size = "medium",
  variationId,
  isVariableProduct = false,
}) => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const { addToCart, cart, isCartUpdating } = useCart();
  const [quantity, setQuantity] = useState(minOrderQuantity);
  const [loading, setLoading] = useState(false);

  // Calculate effective stock (considering items already in cart)
  const getEffectiveStock = () => {
    if (!cart || !cart.items) return stock;

    const cartQuantity = cart.items.reduce((total, item) => {
      // For variable products we need to check option.variationId
      if (options.variationId) {
        return item.productId === productId &&
          item.options?.variationId === options.variationId
          ? total + item.quantity
          : total;
      }
      // For simple products just check productId
      return item.productId === productId ? total + item.quantity : total;
    }, 0);

    return Math.max(0, stock - cartQuantity);
  };

  const effectiveStock = getEffectiveStock();
  const canAddToCart = effectiveStock > 0;
  const isOutOfStock = stock <= 0;

  // Handle quantity change
  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value >= minOrderQuantity && value <= effectiveStock) {
      setQuantity(value);
    }
  };

  // Handle increment quantity
  const handleIncrement = () => {
    if (quantity < effectiveStock) {
      setQuantity(quantity + 1);
    }
  };

  // Handle decrement quantity
  const handleDecrement = () => {
    if (quantity > minOrderQuantity) {
      setQuantity(quantity - 1);
    }
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!canAddToCart) return;

    try {
      setLoading(true);
      await addToCart(productId, quantity, options);
      setLoading(false);
    } catch (error) {
      console.error("Failed to add to cart:", error);
      setLoading(false);
    }
  };

  // If out of stock, disable button
  if (!canAddToCart) {
    return (
      <Button
        variant="contained"
        color="error"
        disabled
        fullWidth={fullWidth}
        startIcon={<ShoppingCartIcon />}
      >
        {tProducts("shared.status.outOfStock")}
      </Button>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", width: "100%", ...sx }}
    >
      {showQuantity && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1,
            width: fullWidth ? "100%" : "auto",
          }}
        >
          <Tooltip title={tProducts("shared.quantity.decrement")}>
            <span>
              <IconButton
                size="small"
                onClick={handleDecrement}
                disabled={
                  quantity <= minOrderQuantity || loading || isCartUpdating
                }
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size="small"
            value={quantity}
            onChange={handleQuantityChange}
            InputProps={{
              inputProps: {
                min: minOrderQuantity,
                max: effectiveStock,
                style: { textAlign: "center" },
              },
            }}
            disabled={loading || isCartUpdating}
            sx={{ width: "60px", mx: 1 }}
          />
          <Tooltip title={tProducts("shared.quantity.increment")}>
            <span>
              <IconButton
                size="small"
                onClick={handleIncrement}
                disabled={
                  quantity >= effectiveStock || loading || isCartUpdating
                }
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {minOrderQuantity > 1 && (
            <Tooltip
              title={tProducts("shared.quantity.minimum", {
                quantity: minOrderQuantity,
              })}
            >
              <Box
                sx={{
                  ml: 1,
                  typography: "caption",
                  color: "text.secondary",
                }}
              >
                Min: {minOrderQuantity}
              </Box>
            </Tooltip>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleAddToCart}
        disabled={loading || isCartUpdating || !canAddToCart}
        fullWidth={fullWidth}
        size={size}
        startIcon={
          loading || isCartUpdating ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <ShoppingCartIcon />
          )
        }
      >
        {buttonText || tProducts("details.addToCart")}
      </Button>
    </Box>
  );
};

AddToCartButton.propTypes = {
  /** Product ID */
  productId: PropTypes.string.isRequired,
  /** Available stock */
  stock: PropTypes.number,
  /** Minimum order quantity */
  minOrderQuantity: PropTypes.number,
  /** Product options */
  options: PropTypes.object,
  /** Button variant */
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  /** Button size */
  size: PropTypes.oneOf(["small", "medium", "large"]),
  /** Whether the button should take full width */
  fullWidth: PropTypes.bool,
  /** Whether to show quantity controls */
  showQuantity: PropTypes.bool,
  /** Button text */
  buttonText: PropTypes.string,
  /** Additional styles */
  sx: PropTypes.object,
  /** Variation ID (for variable products) */
  variationId: PropTypes.string,
  /** Whether this is a variable product */
  isVariableProduct: PropTypes.bool,
};

export default AddToCartButton;
